import { ENUM_FORM_LAYOUT_CONTAINER } from "../components/pageList";
import ExtraFieldsSection from "../components/extraField/ExtraFieldsSection";

const OTHERS_TAB_KEY = "tab-others";
const OTHERS_TAB_LABEL = "Others";
const OTHERS_WIZARD_STEP_KEY = "t1-others";
const DEFAULT_OVERVIEW_TAB_KEY = "tab-overview";

const insertContainerAfterLastChild = (containers, parentKey, newContainer) => {
  let lastIndex = -1;
  containers.forEach((container, index) => {
    if (container.parent === parentKey) lastIndex = index;
  });
  const next = [...containers];
  if (lastIndex === -1) {
    next.push(newContainer);
  } else {
    next.splice(lastIndex + 1, 0, newContainer);
  }
  return next;
};

const addOthersTab = (
  containers,
  { tabsContainerKey, insertTabBeforeKeys }
) =>
  containers.map((container) => {
    if (
      container.key !== tabsContainerKey ||
      container.type !== ENUM_FORM_LAYOUT_CONTAINER.TABS
    ) {
      return container;
    }
    const tab = [...(container.tab || [])];
    if (tab.some((item) => item.key === OTHERS_TAB_KEY)) return container;
    const othersTab = { key: OTHERS_TAB_KEY, label: OTHERS_TAB_LABEL };
    const insertAt = tab.findIndex((item) =>
      insertTabBeforeKeys.includes(item.key)
    );
    const nextTab =
      insertAt === -1
        ? [...tab, othersTab]
        : [...tab.slice(0, insertAt), othersTab, ...tab.slice(insertAt)];
    return {
      ...container,
      tab: nextTab,
    };
  });

const addOthersWizardStep = (
  containers,
  { wizardContainerKey, wizardStepKey, insertWizardBeforeKey }
) =>
  containers.map((container) => {
    if (
      container.key !== wizardContainerKey ||
      container.type !== ENUM_FORM_LAYOUT_CONTAINER.WIZARD
    ) {
      return container;
    }
    const steps = [...(container.steps || [])];
    if (steps.some((step) => step.key === wizardStepKey)) return container;
    const insertAt = steps.findIndex(
      (step) => step.key === insertWizardBeforeKey
    );
    const othersStep = { key: wizardStepKey, label: OTHERS_TAB_LABEL };
    if (insertAt === -1) {
      steps.push(othersStep);
    } else {
      steps.splice(insertAt, 0, othersStep);
    }
    return { ...container, steps };
  });

export const mergeExtraFieldsIntoSchema = (
  schema,
  entityType,
  {
    layout = "tab",
    placement = "OTHER_TAB",
    columns = 1,
    tabsContainerKey = "main-tab",
    wizardContainerKey = "t1",
    wizardStepKey = OTHERS_WIZARD_STEP_KEY,
    insertWizardBeforeKey = "t1-5",
    insertTabBeforeKeys = ["tab-logs", "tab-log"],
    overviewTabKey = DEFAULT_OVERVIEW_TAB_KEY,
    sectionKey = "section-other-fields",
  } = {}
) => {
  if (!schema?.layout) return schema;

  const useOtherTab =
    placement === "OTHER_TAB" ||
    (placement === "OVERVIEW_TAB" && layout === "wizard");

  let containers = [...schema.layout.containers];
  let sectionParent = null;

  if (useOtherTab) {
    if (layout === "tab") {
      containers = addOthersTab(containers, {
        tabsContainerKey,
        insertTabBeforeKeys,
      });
      sectionParent = OTHERS_TAB_KEY;
    } else if (layout === "wizard") {
      containers = addOthersWizardStep(containers, {
        wizardContainerKey,
        wizardStepKey,
        insertWizardBeforeKey,
      });
      sectionParent = wizardStepKey;
    }
  } else if (layout === "tab" && placement === "OVERVIEW_TAB") {
    sectionParent = overviewTabKey;
  }

  if (!sectionParent) return schema;

  const sectionContainer = {
    key: sectionKey,
    parent: sectionParent,
    type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
    props: {
      title: OTHERS_TAB_LABEL,
      withBorder: false,
      withPaper: true,
      mt: layout === "tab" ? 20 : 0,
      mb: 20,
      ml: layout === "wizard" ? 10 : undefined,
    },
  };

  if (!containers.some((container) => container.key === sectionKey)) {
    if (placement === "OVERVIEW_TAB" && layout === "tab") {
      containers = insertContainerAfterLastChild(
        containers,
        overviewTabKey,
        sectionContainer
      );
    } else {
      containers.push(sectionContainer);
    }
  }

  const fields = [
    ...schema.layout.fields,
    {
      name: "extraFieldValues",
      component: ExtraFieldsSection,
      parent: sectionKey,
      props: {
        entityType,
        columns,
      },
    },
  ];

  return {
    ...schema,
    initialValues: {
      ...schema.initialValues,
      extraFieldValues: schema.initialValues?.extraFieldValues ?? {},
    },
    layout: {
      ...schema.layout,
      containers,
      fields,
    },
  };
};
